// A term line's ramp (migration 0085): its periods in order, each so many
// months at its own quantity and, on a Price Grid price, its own option. The
// periods cover the term exactly. Pure: the workspace loads and saves.
import type { BuilderLine } from './quotesApi';

/** One period as the editor holds it. */
export interface DraftPeriod {
  key: string;
  months: number;
  quantity: number;
  /** A Price Grid option chosen for it; null: the one its quantity and the quote's Company Size pick. */
  cellId: string | null;
}

let counter = 0;
const nextKey = () => `p${(counter += 1)}`;

/** The line's periods to edit: its ramp, or one period covering its whole term. */
export function draftOf(line: Pick<BuilderLine, 'term_months' | 'quantity' | 'price_grid_cell_id'> & { ramp_periods?: RampPeriod[] }): DraftPeriod[] {
  if (line.ramp_periods?.length) {
    return line.ramp_periods.map((p) => ({ key: nextKey(), months: p.months, quantity: p.quantity, cellId: p.price_grid_cell_id ?? null }));
  }
  return [{ key: nextKey(), months: line.term_months ?? 12, quantity: line.quantity, cellId: line.price_grid_cell_id ?? null }];
}

/** Splits a period in two at its middle (the first half rounded down); a one-month period cannot be split. */
export function split(draft: DraftPeriod[], key: string): DraftPeriod[] {
  return draft.flatMap((p) => {
    if (p.key !== key || p.months < 2) return [p];
    const first = Math.floor(p.months / 2);
    return [{ ...p, months: first }, { ...p, key: nextKey(), months: p.months - first }];
  });
}

/** Adds a period after the last, taking months from it (half, rounded down) so the term stays covered. */
export function addPeriod(draft: DraftPeriod[]): DraftPeriod[] {
  const last = draft.at(-1);
  if (!last || last.months < 2) return draft;
  return split(draft, last.key);
}

/** Removes a period; its months go to the one before it (the next, for the first). */
export function removePeriod(draft: DraftPeriod[], key: string): DraftPeriod[] {
  const at = draft.findIndex((p) => p.key === key);
  if (at < 0 || draft.length < 2) return draft;
  const into = at === 0 ? 1 : at - 1;
  return draft.flatMap((p, i) => (i === at ? [] : i === into ? [{ ...p, months: p.months + draft[at]!.months }] : [p]));
}

/** Sets one field of one period. */
export function setPeriod(draft: DraftPeriod[], key: string, patch: Partial<Omit<DraftPeriod, 'key'>>): DraftPeriod[] {
  return draft.map((p) => (p.key === key ? { ...p, ...patch } : p));
}

/** Each period's first and last month of the term, in order. */
export function monthsOf(draft: Pick<DraftPeriod, 'months'>[]): { first: number; last: number }[] {
  let next = 1;
  return draft.map((p) => {
    const first = next;
    next += p.months;
    return { first, last: next - 1 };
  });
}

/** What keeps the ramp from saving: its months against the term, or a period without whole months or a quantity. */
export function problem(draft: DraftPeriod[], term: number): string | null {
  if (draft.some((p) => !Number.isInteger(p.months) || p.months < 1)) return 'Each period is a whole number of months.';
  if (draft.some((p) => !(p.quantity > 0))) return 'Each period needs a quantity.';
  const months = draft.reduce((t, p) => t + p.months, 0);
  if (months !== term) return `The periods cover ${months} months of the ${term} month term.`;
  return null;
}

/** The body of PUT …/ramp. One period covering the term is no ramp: it unramps the line. */
export function rampBody(draft: DraftPeriod[]): { periods: { months: number; quantity: number; price_grid_cell_id?: string }[] } {
  if (draft.length < 2) return { periods: [] };
  return { periods: draft.map((p) => ({ months: p.months, quantity: p.quantity, ...(p.cellId ? { price_grid_cell_id: p.cellId } : {}) })) };
}

/** A ramp period as the builder answers it. */
export interface RampPeriod {
  id: string;
  start_month: number;
  months: number;
  quantity: number;
  list_unit_price: number;
  price_grid_cell_id: string | null;
  price_grid_cell_snapshot: { label?: string | null; labels?: string[]; prices_quantity?: boolean } | null;
  net?: number;
  total?: number;
}

/** A period's label on the quote: its months, and its quantity or Price Grid option ("Months 1–6 · 2 Workstreams"). */
export function periodLabel(period: RampPeriod, quantityLabel: string): string {
  const months = period.months === 1 ? `Month ${period.start_month}` : `Months ${period.start_month}–${period.start_month + period.months - 1}`;
  const cell = period.price_grid_cell_snapshot;
  const what = cell?.label ?? (cell?.labels?.length ? cell.labels.join(' · ') : `${period.quantity} ${quantityLabel === 'Quantity' ? 'units' : quantityLabel}`);
  return `${months} · ${what}`;
}
